import * as moment from "moment";

export class DateTimeUtilities {
  public static getLocalDateAndTimeString(utcDateString: string, timezoneOffset: number): string {
    const utcDate = new Date(this.getIEFriendlyDate(utcDateString));
    const tzDate = this.getDateWithOffset(utcDate, timezoneOffset);
    return `${moment(tzDate, [moment.ISO_8601, moment.RFC_2822, "MM/DD/YYYY h:mm:ss A Z"]).format("ddd MMM D, YYYY h:mm A")}`;
  }

  public static getLocalDateAndTimeStringMobile(date: Date, timezoneOffset: number): string {
    const tzDate = this.getDateWithOffset(date, timezoneOffset);
    return `${moment(tzDate, [moment.ISO_8601, moment.RFC_2822, "MM/DD/YYYY h:mm:ss A Z"]).format("M/D/YY h:mm A")}`;
  }

  public static getLocaleShortTimeFromDate(date: Date, includeMeridiems: boolean = true): string {
    return DateTimeUtilities.getShortTimeFromDate(date, new Date().getTimezoneOffset(), includeMeridiems);
  }

  public static getShortTimeFromDate(date: Date, timezoneOffset: number, includeMeridiems: boolean = true): string {
    const newDate = this.getDateWithOffset(date, timezoneOffset);

    let time: string = newDate
      .toLocaleTimeString("en-US", {
        hour: "2-digit",
        minute: "2-digit"
      })
      // GOOD GOD IE!!!! https://www.csgpro.com/blog/2016/08/a-bad-date-with-internet-explorer-11-trouble-with-new-unicode-characters-in-javascript-date-strings
      .replace(/[^A-Za-z 0-9 \.,\?""!@#\$%\^&\*\(\)-_=\+;:<>\/\\\|\}\{\[\]`~]*/g, "");

    if (!includeMeridiems) {
      // We can't guarantee that the client uses meridiems so using
      // a substring can easily break things
      time = time
        .replace(/\s*PM/, "")
        .replace(/\s*AM/, "")
        .replace(/^\s*0/, "");
    }
    return time;
  }

  public static getIEFriendlyDate(dateString: string) {
    const newDateString = dateString.replace(/:(?=\d\d$)/, "");
    return new Date(newDateString);
  }

  public static getShortTimeFromTime(time: string, timezoneOffset: number, includeMeridiems: boolean = true): string {
    const date = this.getDateFromTime(time);
    return this.getShortTimeFromDate(date, timezoneOffset, includeMeridiems);
  }

  public static updateDateTimeTimeZone(dateTime: Date, timezoneOffset: number): string {
    return moment(dateTime, [moment.ISO_8601, moment.RFC_2822, "MM/DD/YYYY h:mm:ss A Z"])
      .utcOffset(timezoneOffset * -1, true)
      .format();
  }

  public static getWeekdayCodeNumber(dayCode: string): number {
    switch (dayCode) {
      case "SUN":
        return 1;
      case "MON":
        return 2;
      case "TUE":
        return 3;
      case "WED":
        return 4;
      case "THU":
        return 5;
      case "FRI":
        return 6;
      case "SAT":
        return 7;
      default:
        return -1;
    }
  }

  public static getDaysBetween(fromDate: Date, toDate: Date): number {
    const millisecondsPerDay = 24 * 3600 * 1000;
    const timeDiff = Math.abs(toDate.getTime() - fromDate.getTime());
    return Math.round(timeDiff / millisecondsPerDay);
  }

  public static getClientOffsetISO(): string {
    const offsetTotalInMinutes = new Date().getTimezoneOffset(); // today
    let offSetHours = Math.abs(offsetTotalInMinutes / 60);
    let offsetMin = Math.abs(offsetTotalInMinutes % 60);
    let offSetHoursString = "";
    let offSetMinutesString = "";
    let isoOffset = "";

    if (offSetHours < 10) {
      offSetHoursString = "0" + offSetHours;
    }

    if (offsetMin < 10) {
      offSetMinutesString = "0" + offsetMin;
    }

    // Add an opposite sign to the offset
    // If offset is 0, it means timezone is UTC
    if (offsetTotalInMinutes < 0) {
      isoOffset = "+" + offSetHoursString + ":" + offSetMinutesString;
    } else if (offsetTotalInMinutes > 0) {
      isoOffset = "-" + offSetHoursString + ":" + offSetMinutesString;
    } else if (offsetTotalInMinutes == 0) {
      isoOffset = "Z";
    }

    return isoOffset;
  }

  public static getClientOffsetISOForDate(someDate: Date): string {
    const offsetTotalInMinutes = new Date(someDate).getTimezoneOffset(); // future appt day
    let offSetHours = Math.abs(offsetTotalInMinutes / 60);
    let offsetMin = Math.abs(offsetTotalInMinutes % 60);
    let offSetHoursString = "";
    let offSetMinutesString = "";
    let isoOffset = "";

    if (offSetHours < 10) {
      offSetHoursString = "0" + offSetHours;
    }

    if (offsetMin < 10) {
      offSetMinutesString = "0" + offsetMin;
    }

    // Add an opposite sign to the offset
    // If offset is 0, it means timezone is UTC
    if (offsetTotalInMinutes < 0) {
      isoOffset = "+" + offSetHoursString + ":" + offSetMinutesString;
    } else if (offsetTotalInMinutes > 0) {
      isoOffset = "-" + offSetHoursString + ":" + offSetMinutesString;
    } else if (offsetTotalInMinutes == 0) {
      isoOffset = "Z";
    }
    return isoOffset;
  }

  /**
   * Converts branch UTC offset (in minutes, positive = behind UTC) to ISO offset string
   * Mirrors getClientOffsetISO() but takes a number instead of reading browser timezone
   * Examples: 480 → "-08:00" (PST), 420 → "-07:00" (PDT), 0 → "Z"
   */
  public static getBranchOffsetISO(utcOffsetMinutes: number): string {
    const offSetHours = Math.abs(Math.floor(utcOffsetMinutes / 60));
    const offsetMin = Math.abs(utcOffsetMinutes % 60);
    const offSetHoursString = offSetHours < 10 ? "0" + offSetHours : "" + offSetHours;
    const offSetMinutesString = offsetMin < 10 ? "0" + offsetMin : "" + offsetMin;

    if (utcOffsetMinutes === 0) return "Z";
    return (utcOffsetMinutes > 0 ? "-" : "+") + offSetHoursString + ":" + offSetMinutesString;
  }

  /**
   * Formats a Date with the branch's UTC offset (not the browser's)
   * Replaces updateDateTimeTimeZone for submission points - uses moment WITHOUT the 'true' flag
   * so it converts the UTC moment to the target offset rather than reinterpreting the browser's local display time
   * A correctly UTC-anchored Date (18:00 UTC for 10 AM PST) produces "2025-03-10T10:00:00-08:00" from any browser
   */
  public static formatWithBranchOffset(dateTime: Date, utcOffsetMinutes: number): string {
    return moment(dateTime)
      .utcOffset(utcOffsetMinutes * -1)  // no 'true' flag — convert, don't reinterpret
      .format();
  }

  private static getDateWithOffset(date: Date, timezoneOffset: number): Date {
    const currentOffset = date.getTimezoneOffset();
    const minutesToAdd = currentOffset - timezoneOffset;
    return new Date(date.getTime() + minutesToAdd * 60000);
  }

  private static getDateFromTime(time: string) {
    const today = new Date();
    const timeParts = time.split(":");
    const hour = parseInt(timeParts[0], 10);
    const minutes = parseInt(timeParts[1], 10);
    return new Date(Date.UTC(today.getUTCFullYear(), today.getUTCMonth(), today.getUTCDate(), hour, minutes));
  }
}
